import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { adminAPI, plansAPI } from '../api/client';
import { AdminUser } from '../types';
import { traceError } from '../lib/trace';
import { HelpLink } from '../components/layout/HelpLink';

export function Admin() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<AdminUser | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['admin-users'], queryFn: adminAPI.listUsers });
  const { data: catalogue } = useQuery({ queryKey: ['plans'], queryFn: plansAPI.list });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-users'] });
  };

  const setPlan = useMutation({
    mutationFn: ({ teamId, plan }: { teamId: string; plan: string }) =>
      adminAPI.setTeamPlan(teamId, plan),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e: unknown) => {
      traceError('admin', 'setting plan failed', { error: e });
      setError(e instanceof Error ? e.message : String(e));
    },
  });

  const deleteUser = useMutation({
    // cascade is only ever sent from the confirmation step below, so a user
    // who owns teams can never be removed by a single stray click.
    mutationFn: ({ id, cascade }: { id: string; cascade: boolean }) =>
      adminAPI.deleteUser(id, cascade),
    onSuccess: () => {
      setError(null);
      setConfirming(null);
      refresh();
    },
    onError: (e: unknown) => {
      traceError('admin', 'deleting user failed', { error: e });
      setError(e instanceof Error ? e.message : String(e));
      setConfirming(null);
    },
  });

  const users = data?.users ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      [u.email, u.display_name, u.team_name, u.team_plan]
        .filter(Boolean)
        .some(field => String(field).toLowerCase().includes(q))
    );
  }, [users, search]);

  if (isLoading) {
    return <div className="loading-center" />;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <h1 className="page-title">Admin</h1>
        <HelpLink topic="admin" label="Admin" />
      </div>
          <p className="page-subtitle">
            {users.length} user{users.length === 1 ? '' : 's'} across the platform.
          </p>
        </div>
        <input
          type="search"
          className="form-input"
          placeholder="Search email, name, team or plan…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
      </div>

      {error && (
        <div className="plans-error" role="alert">
          {error}
        </div>
      )}

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>User</th>
              <th>Team</th>
              <th>Plan</th>
              <th>Monitors</th>
              <th>Joined</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {filtered.map(user => (
              <tr key={user.id}>
                <td>
                  <div style={{ fontWeight: 600 }}>{user.display_name || user.email}</div>
                  <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>
                    {user.email} · {user.oauth_provider}
                  </div>
                </td>
                <td>
                  {user.team_name || <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
                  {user.team_role && (
                    <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>
                      {user.team_role}
                    </div>
                  )}
                </td>
                <td>
                  {user.team_id ? (
                    <select
                      className="form-input"
                      value={user.team_plan ?? 'free'}
                      disabled={setPlan.isPending}
                      onChange={e =>
                        setPlan.mutate({ teamId: user.team_id!, plan: e.target.value })
                      }
                    >
                      {(catalogue?.plans ?? []).map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                  )}
                </td>
                <td>{user.monitor_count}</td>
                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    aria-label={`Delete ${user.email}`}
                    onClick={() => setConfirming(user)}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="empty-state">
            <p>No users match “{search}”.</p>
          </div>
        )}
      </div>

      {confirming && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Confirm deletion">
          <div className="modal">
            <h2 className="card-title">Delete {confirming.email}?</h2>
            <p style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-4) 0' }}>
              This removes the account permanently. If they own{' '}
              <strong>{confirming.team_name || 'a team'}</strong>, that team and every monitor in it
              go with them.
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirming(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={deleteUser.isPending}
                onClick={() => deleteUser.mutate({ id: confirming.id, cascade: true })}
              >
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
